import history from "history";
import { parse } from "query-string";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import formatUrl from "utils/formatUrl";
import { sanitizeRedirect } from "utils/sanitizeRedirect";

import config from "config";
import { AccountSettings } from "pages/AccountSettings";
import ClientInvite from "pages/ClientInvite";
import { ClientSelect } from "pages/ClientSelect";
import InviteLogin from "pages/InviteLogin";
import Login from "pages/Login";
import ForgotPassword from "pages/Login/ForgotPassword";
import ResetPassword from "pages/Login/ResetPassword";
import VerifyEmail from "pages/Login/VerifyEmail";
import NotFound from "pages/NotFound";
import Register from "pages/Register";
import { UserManagement } from "pages/UserManagement";

import AuthorizedRoute from "../AuthorizedRoute";

function AppRouter({ location }: { location?: history.Location }) {
  const history = useHistory();
  const queryParams = Object.fromEntries(
    new URLSearchParams(window.location.search)
  );

  const signinSuccessRedirect = (location: string) => {
    const parsed = parse(location);
    const redirect = parsed.redirectUrl
      ? String(parsed.redirectUrl)
      : undefined;

    if (!redirect) {
      window.location.href = config.PORTAL_URL;
      return;
    }
    if (redirect[0] === "/") {
      history.push(redirect);
    } else {
      const sanitizedRedirect = sanitizeRedirect(redirect);
      if (sanitizedRedirect) {
        window.location.href = sanitizedRedirect;
      }
    }
    return null;
  };

  return (
    <Switch location={location}>
      <Route
        exact
        path="/"
        component={() => {
          window.location.href = config.PORTAL_URL;
          return null;
        }}
      />
      <AuthorizedRoute
        exact
        path="/settings/users"
        component={UserManagement}
        permissionsToView={["View"]}
      />
      <AuthorizedRoute exact path="/select" component={ClientSelect} />
      <Route exact path="/invite/:inviteId/signin" component={InviteLogin} />
      <Route exact path="/invite/:inviteId/accept" component={ClientInvite} />
      <Route exact path="/invite/:inviteId/register" component={Register} />
      {/* Identity redirects to /login after sign out and adds /login to invite email links, so we need this redirect until that is changed
      See: https://dev.azure.com/codat/Codat/_git/Identity?path=/Codat.Identity.Api/Queries/User/GetUserLoginRedirectQuery.cs&version=GBmain&_a=contents
      */}
      <Redirect
        exact
        path="/login"
        to={formatUrl("/signin", {}, queryParams)}
      />
      <Route exact path="/signin" component={Login} />
      <Route exact path="/verify/email" component={VerifyEmail} />
      <Route exact path="/reset/password" component={ResetPassword} />
      <Route exact path="/forgot/password" component={ForgotPassword} />
      {/* User is redirected to this page after accepting an invite. This ensures they aren't taken to the signin page after registering */}
      <AuthorizedRoute
        exact
        path="/signin/success"
        render={(p) => signinSuccessRedirect(p.location.search)}
      />
      <AuthorizedRoute
        exact
        path="/settings/account"
        component={AccountSettings}
      />
      <Route component={NotFound} />
    </Switch>
  );
}

export default AppRouter;
